import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getFundBasketLinkedSipCartStatus,
  getFundBasketLinkedSipChanges,
  getFundBasketLinkedSips,
  getFundBasketVersions,
  rebalanceFundBasketLinkedSips,
} from "../../api/funds/rebalance";
import { CustomQueryOptions, FundBasketRebalanceRequest } from "../../types";
import { fundBasketKeys } from "./common";

export const fundBasketRebalanceKeys = {
  linkedSips: (basketId: number) =>
    [...fundBasketKeys.detail(basketId), "linked-sips"] as const,
  linkedSipChanges: (basketId: number, clientId: number) =>
    [
      ...fundBasketRebalanceKeys.linkedSips(basketId),
      clientId,
      "changes",
    ] as const,
  linkedSipCartStatus: (basketId: number, clientId: number) =>
    [
      ...fundBasketRebalanceKeys.linkedSips(basketId),
      clientId,
      "cart-status",
    ] as const,
  versions: (basketId: number) =>
    [...fundBasketKeys.detail(basketId), "versions"] as const,
};

export const fundBasketLinkedSipsQueryOptions = (
  basketId: number,
  options?: CustomQueryOptions<typeof getFundBasketLinkedSips>
) =>
  queryOptions({
    queryKey: fundBasketRebalanceKeys.linkedSips(basketId),
    queryFn: () => getFundBasketLinkedSips(basketId),
    placeholderData: (prev) => prev,
    ...options,
    enabled: options?.enabled ?? !!basketId,
  });

export function useFundBasketLinkedSips(
  ...params: Parameters<typeof fundBasketLinkedSipsQueryOptions>
) {
  return useQuery(fundBasketLinkedSipsQueryOptions(...params));
}

export const fundBasketLinkedSipChangesQueryOptions = (
  variables: { basketId: number; clientId: number },
  options?: CustomQueryOptions<typeof getFundBasketLinkedSipChanges>
) =>
  queryOptions({
    queryKey: fundBasketRebalanceKeys.linkedSipChanges(
      variables.basketId,
      variables.clientId
    ),
    queryFn: () => getFundBasketLinkedSipChanges(variables),
    ...options,
    enabled: options?.enabled ?? !!(variables.basketId && variables.clientId),
  });

export function useFundBasketLinkedSipChanges(
  ...params: Parameters<typeof fundBasketLinkedSipChangesQueryOptions>
) {
  return useQuery(fundBasketLinkedSipChangesQueryOptions(...params));
}

export const fundBasketLinkedSipCartStatusQueryOptions = (
  variables: { basketId: number; clientId: number },
  options?: CustomQueryOptions<typeof getFundBasketLinkedSipCartStatus>
) =>
  queryOptions({
    queryKey: fundBasketRebalanceKeys.linkedSipCartStatus(
      variables.basketId,
      variables.clientId
    ),
    queryFn: () => getFundBasketLinkedSipCartStatus(variables),
    ...options,
    enabled: options?.enabled ?? !!(variables.basketId && variables.clientId),
  });

export function useFundBasketLinkedSipCartStatus(
  ...params: Parameters<typeof fundBasketLinkedSipCartStatusQueryOptions>
) {
  return useQuery(fundBasketLinkedSipCartStatusQueryOptions(...params));
}

export const fundBasketVersionsQueryOptions = (
  basketId: number,
  options?: CustomQueryOptions<typeof getFundBasketVersions>
) =>
  queryOptions({
    queryKey: fundBasketRebalanceKeys.versions(basketId),
    queryFn: () => getFundBasketVersions(basketId),
    ...options,
    enabled: options?.enabled ?? !!basketId,
  });

export function useFundBasketVersions(
  ...params: Parameters<typeof fundBasketVersionsQueryOptions>
) {
  return useQuery(fundBasketVersionsQueryOptions(...params));
}

export function useRebalanceFundBasketLinkedSips(basketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FundBasketRebalanceRequest) =>
      rebalanceFundBasketLinkedSips({ basketId, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fundBasketRebalanceKeys.linkedSips(basketId),
      });
    },
  });
}
